import React, { useCallback } from "react";
import { StyleSheet, View, Text, StyleProp, ViewStyle } from "react-native";

import { Appbar, useTheme, Menu } from "react-native-paper";

import { dailyChallengesService } from "../services/DailyChallengesService";

import { knownAchievementIds } from "../features/achievements";
import { unifiedDataStore } from "../services/UnifiedDataStore";

import { useModalStore } from "../stores/useModalStore";
import { useDailyChallengeStore } from "../stores/useDailyChallengeStore";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import { APP_VERSION } from "../utils/appVersion";
import AriadneHeaderMark from "./AriadneHeaderMark";
import DevBadge from "./dev/DevBadge";
import CustomIcon from "./CustomIcon";
import HeaderMenu from "./HeaderMenu";

interface AppHeaderProps {
  onNewGame: () => void;
  onGiveUp: () => void;
  newGameDisabled?: boolean;
  giveUpDisabled?: boolean;
  appReady?: boolean;
}

// Simple custom action component without animations - memoized to prevent unnecessary re-renders
const CustomAppbarAction = React.memo<{
  icon: string;
  onPress: () => void;
  disabled?: boolean;
  color: string;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
}>(({ icon, onPress, disabled, color, style, accessibilityLabel, testID }) => (
  <Appbar.Action
    onPress={onPress}
    accessibilityLabel={accessibilityLabel}
    testID={testID}
    disabled={disabled}
    style={style}
    animated={false}
    color={color}
    icon={({ color: iconColor, size }) => (
      <CustomIcon source={icon} size={size} color={iconColor} />
    )}
  />
));

const AppHeader: React.FC<AppHeaderProps> = ({
  onNewGame,
  onGiveUp,
  newGameDisabled = false,
  giveUpDisabled = false,
  appReady = true,
}) => {
  const { customColors, colors } = useTheme() as ExtendedTheme;

  // Modal state and actions from dedicated modal store
  const setQuickstartModalVisible = useModalStore(
    (state) => state.setQuickstartModalVisible,
  );
  const setContactModalVisible = useModalStore(
    (state) => state.setContactModalVisible,
  );
  const statsModalVisible = useModalStore((state) => state.statsModalVisible);
  const setStatsModalVisible = useModalStore(
    (state) => state.setStatsModalVisible,
  );
  const setDailiesModalVisible = useModalStore(
    (state) => state.setDailiesModalVisible,
  );

  // State for tracking menu visibility and notification counts
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [unviewedAchievementCount, setUnviewedAchievementCount] =
    React.useState(0);
  const [unviewedWordCollectionCount, setUnviewedWordCollectionCount] =
    React.useState(0);
  const [hasIncompleteDailyChallenge, setHasIncompleteDailyChallenge] =
    React.useState(false);

  // Function to refresh unviewed achievements
  const refreshUnviewedAchievements = React.useCallback(async () => {
    try {
      const unlockedIds = knownAchievementIds(
        await unifiedDataStore.getUnlockedAchievements(),
      );
      const viewedIds = await unifiedDataStore.getViewedAchievementIds();
      const unviewedCount = unlockedIds.filter(
        (id) => !viewedIds.includes(id),
      ).length;
      setUnviewedAchievementCount(unviewedCount);
    } catch (error) {
      console.error("Error refreshing unviewed achievements:", error);
    }
  }, []);

  // Function to refresh unviewed word collections
  const refreshUnviewedWordCollections = React.useCallback(async () => {
    try {
      const completedIds = await unifiedDataStore.getCompletedWordCollections();
      const viewedIds = await unifiedDataStore.getViewedWordCollections();
      const unviewedCount = completedIds.filter(
        (id) => !viewedIds.includes(id),
      ).length;
      setUnviewedWordCollectionCount(unviewedCount);
    } catch (error) {
      console.error("Error refreshing unviewed word collections:", error);
    }
  }, []);

  // Function to check for incomplete daily challenge
  const checkIncompleteDailyChallenge = React.useCallback(async () => {
    try {
      console.log("🔍 AppHeader: checkIncompleteDailyChallenge called");

      // First, check if today's challenge is available and completed
      const todaysChallenge = dailyChallengesService.getTodaysChallenge();
      console.log("🔍 AppHeader: Checking today's challenge completion:", {
        todaysChallengeExists: !!todaysChallenge,
        challengeId: todaysChallenge?.id,
      });

      if (!todaysChallenge) {
        console.log(
          "🔍 AppHeader: No today's challenge, setting hasIncompleteDailyChallenge = false",
        );
        setHasIncompleteDailyChallenge(false);
        return;
      }

      // Check both the persistent storage AND the game store state
      const hasCompletedPersistent =
        await dailyChallengesService.hasCompletedTodaysChallenge();
      const hasCompletedInStore =
        useDailyChallengeStore.getState().hasPlayedToday;

      console.log("🔍 AppHeader: Completion check:", {
        hasCompletedPersistent,
        hasCompletedInStore,
        challengeId: todaysChallenge.id,
      });

      // If either indicates completion, consider it completed
      const hasCompleted = hasCompletedPersistent || hasCompletedInStore;

      if (hasCompleted) {
        console.log(
          "🔍 AppHeader: Challenge is completed, setting hasIncompleteDailyChallenge = false",
        );
        setHasIncompleteDailyChallenge(false);
        return;
      }

      // Only if the challenge is NOT completed, then check for unfinished games
      const { unifiedDataStore } = await import("../services/UnifiedDataStore");
      const savedGame = await unifiedDataStore.loadCurrentGame("regular"); // Regular game storage (daily challenges now use isChallenge: false)

      console.log("🔍 AppHeader: savedGame:", {
        exists: !!savedGame,
        gameStatus: savedGame?.gameStatus,
        isDailyChallenge: savedGame?.isDailyChallenge,
        challengeId: savedGame?.currentDailyChallengeId,
      });

      if (
        savedGame &&
        savedGame.gameStatus === "playing" &&
        savedGame.isDailyChallenge === true &&
        savedGame.currentDailyChallengeId === todaysChallenge.id
      ) {
        console.log("🔍 AppHeader: Found unfinished daily challenge game");
        setHasIncompleteDailyChallenge(true);
      } else {
        console.log("🔍 AppHeader: No unfinished daily challenge game");
        setHasIncompleteDailyChallenge(false);
      }
    } catch (error) {
      console.error("Error checking daily challenge status:", error);
      setHasIncompleteDailyChallenge(false);
    }
  }, []);

  // Load notification state on mount
  React.useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load unviewed achievements
        await refreshUnviewedAchievements();

        // Load unviewed word collections
        await refreshUnviewedWordCollections();

        // Check daily challenge status
        await checkIncompleteDailyChallenge();

        // Premium status checked when needed
      } catch (error) {
        console.error("Error loading header data:", error);
      }
    };

    loadInitialData();
  }, [
    refreshUnviewedAchievements,
    refreshUnviewedWordCollections,
    checkIncompleteDailyChallenge,
  ]);

  // Watch for stats modal closing and refresh unviewed achievements and word collections
  React.useEffect(() => {
    if (!statsModalVisible) {
      // Modal just closed, refresh unviewed achievements and word collections
      refreshUnviewedAchievements();
      refreshUnviewedWordCollections();
    }
  }, [
    statsModalVisible,
    refreshUnviewedAchievements,
    refreshUnviewedWordCollections,
  ]);

  // Watch for dailies modal closing and refresh daily challenge status
  const dailiesModalVisible = useModalStore(
    (state) => state.dailiesModalVisible,
  );
  React.useEffect(() => {
    if (!dailiesModalVisible) {
      // Modal just closed, refresh daily challenge status
      checkIncompleteDailyChallenge();
    }
  }, [dailiesModalVisible, checkIncompleteDailyChallenge]);

  // Also refresh achievements periodically when modal is visible (to catch real-time unlocks)
  React.useEffect(() => {
    if (statsModalVisible) {
      const interval = setInterval(() => {
        refreshUnviewedAchievements();
        refreshUnviewedWordCollections();
      }, 2000); // Check every 2 seconds
      return () => clearInterval(interval);
    }
  }, [
    statsModalVisible,
    refreshUnviewedAchievements,
    refreshUnviewedWordCollections,
  ]);

  // Refresh achievements, word collections, and daily challenge status when component is focused/visible
  React.useEffect(() => {
    const interval = setInterval(() => {
      refreshUnviewedAchievements();
      refreshUnviewedWordCollections();
      checkIncompleteDailyChallenge();
    }, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [
    refreshUnviewedAchievements,
    refreshUnviewedWordCollections,
    checkIncompleteDailyChallenge,
  ]);

  // Get the highest priority color among all notifications
  // Priority order: Gold (achievements + word collections) > Daily Challenge
  const getHighestPriorityColor = () => {
    // HIGHEST PRIORITY: Unviewed achievements OR completed word collections (gold)
    if (unviewedAchievementCount > 0 || unviewedWordCollectionCount > 0) {
      return customColors.achievementIcon;
    }

    // SECOND PRIORITY: Incomplete daily challenge (blue)
    if (hasIncompleteDailyChallenge) {
      return customColors.currentNode;
    }

    // Default fallback
    return customColors.endNode;
  };

  // The menu dot is decorative; say what it means in the menu button's name.
  const menuNotices = [
    unviewedAchievementCount + unviewedWordCollectionCount > 0
      ? "new unlocks"
      : null,
    hasIncompleteDailyChallenge ? "today's puzzle not played" : null,
  ].filter(Boolean);
  const menuLabel = menuNotices.length
    ? `Open menu, ${menuNotices.join(", ")}`
    : "Open menu";

  // Handlers
  const handleMenuPress = () => {
    setMenuVisible(true);
  };

  const closeMenu = useCallback(() => setMenuVisible(false), []);

  const handleQuickstart = () => {
    setMenuVisible(false);
    setQuickstartModalVisible(true);
  };

  const handleStats = () => {
    setMenuVisible(false);
    setStatsModalVisible(true);
  };

  const handleDailies = () => {
    setMenuVisible(false);
    setDailiesModalVisible(true);
    // Clear daily challenge notification immediately when opening modal
    checkIncompleteDailyChallenge();
  };

  const handleContact = () => {
    setMenuVisible(false);
    setContactModalVisible(true);
  };

  // Stable component definitions to avoid re-renders

  const MenuTitleWithBadge = useCallback(
    ({
      title,
      badgeCount,
      badgeColor,
    }: {
      title: string;
      badgeCount?: number;
      badgeColor?: string;
    }) => (
      <View style={styles.menuTitleWithBadge}>
        <Text style={{ color: colors.onSurface }}>{title}</Text>
        {badgeCount !== undefined && badgeColor && (
          <View
            accessibilityLabel={`${badgeCount} new`}
            style={[
              styles.menuBadgeSticker,
              { backgroundColor: badgeColor, borderColor: colors.surface },
            ]}
          >
            <Text style={styles.menuBadgeText}>{badgeCount}</Text>
          </View>
        )}
      </View>
    ),
    [colors.onSurface, colors.surface],
  );

  const DailyChallengeTitle = useCallback(
    () =>
      hasIncompleteDailyChallenge ? (
        <View style={styles.menuTitleWithBadge}>
          <Text style={{ color: colors.onSurface }}>Daily Challenges</Text>
          <View accessible accessibilityLabel="Today's puzzle not played yet">
            <CustomIcon
              source="circle-outline"
              size={16}
              color={colors.primary}
            />
          </View>
        </View>
      ) : (
        "Daily Challenges"
      ),
    [hasIncompleteDailyChallenge, colors.onSurface, colors.primary],
  );

  const StatsTitle = useCallback(
    () =>
      unviewedAchievementCount > 0 || unviewedWordCollectionCount > 0 ? (
        <MenuTitleWithBadge
          title="Stats"
          badgeCount={unviewedAchievementCount + unviewedWordCollectionCount}
          badgeColor={customColors.achievementIcon}
        />
      ) : (
        "Stats"
      ),
    [
      unviewedAchievementCount,
      unviewedWordCollectionCount,
      customColors.achievementIcon,
      MenuTitleWithBadge,
    ],
  );

  // Icon components
  const CalendarIcon = useCallback(
    () => (
      <CustomIcon source="calendar-today" size={24} color={colors.onSurface} />
    ),
    [colors.onSurface],
  );

  const TrophyIcon = useCallback(
    () => <CustomIcon source="trophy" size={24} color={colors.onSurface} />,
    [colors.onSurface],
  );

  const HelpIcon = useCallback(
    () => (
      <CustomIcon
        source="help-circle-outline"
        size={24}
        color={colors.onSurface}
      />
    ),
    [colors.onSurface],
  );

  const EmailIcon = useCallback(
    () => <CustomIcon source="email" size={24} color={colors.onSurface} />,
    [colors.onSurface],
  );

  return (
    <Appbar.Header>
      {/* Inner row keeps the logo and actions near the 600px play column on desktop */}
      <View style={styles.inner}>
        {/* Her mark stands where the logo was; it is the thread toggle (spec §5, D4) */}
        <AriadneHeaderMark />

        {/* Title */}
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text style={[styles.title, { color: colors.onSurface }]}>
            Synapse
          </Text>
          <Text style={[styles.versionTag, { color: colors.onSurface }]}>
            v{APP_VERSION}
          </Text>
          <DevBadge />
        </View>

        {/* Spacer to push action buttons to the right */}
        <View style={{ flex: 1 }} />

        {/* Hamburger Menu */}
        <View style={styles.menuContainer}>
          {/* Removed auth/account button - local-only mode */}

          <HeaderMenu
            visible={menuVisible}
            onDismiss={closeMenu}
            anchor={
              <CustomAppbarAction
                icon="menu"
                accessibilityLabel={menuLabel}
                style={styles.action}
                testID="menu-button"
                onPress={handleMenuPress}
                color={colors.onSurface}
                disabled={!appReady}
              />
            }
            contentStyle={{ backgroundColor: colors.surface }}
          >
            <Menu.Item
              onPress={handleDailies}
              title={DailyChallengeTitle()}
              leadingIcon={CalendarIcon}
              titleStyle={{ color: colors.onSurface }}
            />
            <Menu.Item
              onPress={handleStats}
              title={StatsTitle()}
              leadingIcon={TrophyIcon}
              titleStyle={{ color: colors.onSurface }}
            />
            <Menu.Item
              onPress={handleQuickstart}
              title="How to Play"
              leadingIcon={HelpIcon}
              titleStyle={{ color: colors.onSurface }}
            />
            <Menu.Item
              onPress={handleContact}
              title="Contact"
              leadingIcon={EmailIcon}
              titleStyle={{ color: colors.onSurface }}
            />
          </HeaderMenu>
          {(unviewedAchievementCount > 0 ||
            unviewedWordCollectionCount > 0 ||
            hasIncompleteDailyChallenge) && (
            <View
              testID="menu-notification-dot"
              // Decorative: it overlaps the hamburger's corner, so let presses through.
              pointerEvents="none"
              style={[
                styles.notificationBadge,
                { borderColor: colors.surface },
                // If it's ONLY a daily challenge notification, make it hollow
                unviewedAchievementCount === 0 &&
                unviewedWordCollectionCount === 0 &&
                hasIncompleteDailyChallenge
                  ? {
                      backgroundColor: "transparent",
                      borderColor: colors.primary,
                      borderWidth: 2,
                    }
                  : { backgroundColor: getHighestPriorityColor() },
              ]}
            />
          )}
        </View>

        {/* New Game Button */}
        <CustomAppbarAction
          icon={"refresh"}
          onPress={onNewGame}
          disabled={newGameDisabled || !appReady}
          color={customColors.startNode}
          accessibilityLabel="New game"
          testID="new-game-button"
          style={styles.action}
        />

        {/* Give Up Button */}
        <CustomAppbarAction
          icon={"flag-variant"}
          onPress={onGiveUp}
          disabled={giveUpDisabled || !appReady}
          color={customColors.endNode}
          accessibilityLabel="Give up"
          testID="give-up-button"
          style={styles.action}
        />
      </View>
    </Appbar.Header>
  );
};

const styles = StyleSheet.create({
  inner: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    maxWidth: 960,
    marginHorizontal: "auto",
  },
  // 44x44 hit area for each header action
  action: {
    width: 44,
    height: 44,
    margin: 0,
  },
  title: {
    fontWeight: "bold",
    fontSize: 24,
  },
  versionTag: {
    fontSize: 12,
    opacity: 0.6,
    marginLeft: 6,
    marginBottom: 4,
    alignSelf: "flex-end",
  },
  logoContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  notificationBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
  },
  iconContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  menuContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  menuTitleWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  menuBadgeSticker: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 4,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
  },
  // Near-black on the pastel badge: 13:1+ (white was ~1.2:1 on gold)
  menuBadgeText: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#121212",
  },
});

export default AppHeader;
